class WishlistItems extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
    this.itemsContainer = this;
    this.emptyMessage = this.querySelector('.wishlist__empty');
    this.productGrid = this.querySelector('.product-grid');
    this.skeletonItems = document.querySelectorAll('.wishlist__skeleton-item');
    this.buttonObservers = new Map();

    this.setupWishlistButtonListener();
    this.init();

    // listen to the predictive-search-results-rendered event
    window.addEventListener('predictive-search-results-rendered', () => {
      const wishlistButtons = document.querySelectorAll('#predictive-search-results .th_wl_col_btn');
      if (wishlistButtons.length > 0) {
        wishlistButtons.forEach(button => {
          button.innerHTML = `<div class="d-flex"><div class="th_wl_button" onclick="addToWishlist(this, event)"><svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M25.9584 10.7645C25.9583 15.6823 22.0292 19.988 14.7827 24.5947L14 25.0915L13.2173 24.5947C5.97083 19.988 2.04177 15.6823 2.04169 10.7645C2.04169 6.98493 4.41642 3.21875 8.66115 3.21875C11.301 3.21879 13.0128 4.74259 14 6.11149C14.9872 4.74259 16.699 3.21879 19.3389 3.21875C23.5836 3.21875 25.9584 6.98493 25.9584 10.7645Z" fill="#E33074"></path></svg></div></div>`
        });
      }
    });
  }

  setupWishlistButtonListener() {
    document.addEventListener('click', (event) => {
      const wishlistButton = event.target.closest('.th_wl_col_btn');

      if (wishlistButton) {
        const productId = wishlistButton.dataset.product_id;

        if (productId) {
          let productElement = null;
          if (event.target.closest('.predictive-search__results-list')) {
            productElement = document.querySelector(`.predictive-search__results-list .card-wrapper[data-product-id="${productId}"]`)
          } else {
            productElement = this.productGrid.querySelector(`.card-wrapper[data-product-id="${productId}"]`);
          }

          if (productElement !== null) {
            const gridItem = productElement.closest('.grid__item');
            if (gridItem) {
              gridItem.classList.add('hidden');
              this.checkEmptyState();
              this.observeWishlistButton(productId, gridItem, wishlistButton);
            }
          }
        }
      }
    });
  }

  observeWishlistButton(productId, gridItem, wishlistButton) {
    if (this.buttonObservers.has(productId)) {
      this.buttonObservers.get(productId).disconnect();
    }

    const observer = new MutationObserver((mutations) => {
      this.verifyRemoval(productId, gridItem, wishlistButton);
      observer.disconnect();
      this.buttonObservers.delete(productId);
    });

    observer.observe(wishlistButton, {
      childList: true,
      subtree: true,
      characterData: true
    });

    this.buttonObservers.set(productId, observer);
  }

  verifyRemoval(productId, gridItem, wishlistButton) {
    try {
      const isUnwishlisted = wishlistButton.querySelector('.th_wl_button') !== null;
      const isStillWishlisted = wishlistButton.querySelector('.th_wl_button_after') !== null;

      if (isStillWishlisted) {
        gridItem.classList.remove('hidden');
      } else if (isUnwishlisted) {
        gridItem.remove();
      } else {
        const wishlistData = localStorage.getItem('th_wishlist_products_list');
        if (!wishlistData) {
          gridItem.remove();
          this.checkEmptyState();
          return;
        }

        const wishlistItems = JSON.parse(wishlistData);
        const item = wishlistItems.find(item =>
          item.shopify_product_id === parseInt(productId) ||
          item.shopify_product_id === productId
        );

        if (item && item.wishlisted === true) {
          gridItem.classList.remove('hidden');
        } else {
          gridItem.remove();
        }
      }

      this.checkEmptyState();
    } catch (error) {
      console.error('Error verifying item removal:', error);
      gridItem.classList.remove('hidden');
    }
  }

  checkEmptyState() {
    const wishlistCount = localStorage.getItem('th_wlc_pro_count');
    const hasProductCount = wishlistCount && parseInt(wishlistCount) > 0;
    const hasVisibleProducts = this.productGrid.querySelectorAll('.grid__item:not(.hidden):not(.wishlist__skeleton-item)').length > 0;

    if (!hasProductCount || !hasVisibleProducts) {
      this.showEmptyMessage();
    } else {
      this.hideEmptyMessage();
    }
  }

  async init() {
    let wishlistItems = this.getWishlistFromStorage();

    if (wishlistItems.length > 0) {
      await this.renderWishlistItems(wishlistItems);
      this.hideSkeletons();
    } else {
      this.hideSkeletons();
      this.showEmptyMessage();
    }
  }

  hideSkeletons() {
    if (this.skeletonItems && this.skeletonItems.length) {
      this.skeletonItems.forEach(item => {
        item.classList.add('hidden');
      });
    }
  }

  showEmptyMessage() {
    if (this.emptyMessage) {
      this.emptyMessage.classList.add('visible');
    }
  }

  hideEmptyMessage() {
    if (this.emptyMessage) {
      this.emptyMessage.classList.remove('visible');
    }
  }

  getWishlistFromStorage() {
    try {
      const wishlistData = localStorage.getItem('th_wishlist_products_list');
      if (!wishlistData) return [];

      const items = JSON.parse(wishlistData);

      return items.filter(item => {
        const hasProductId = item.shopify_product_id && item.shopify_product_id !== null;
        const isWishlisted = item.wishlisted === true;
        return hasProductId && isWishlisted;
      }).map(item => ({
        product_id: item.shopify_product_id,
        productId: item.shopify_product_id,
        variant_id: item.shopify_variant_id
      }));

    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return [];
    }
  }

  async renderWishlistItems(items) {
    if (!items || items.length === 0) return;

    const productIds = items.map(item => item.product_id || item.productId);

    try {
      let url = (window.Shopify?.routes?.root || '/') + 'search?view=wishlist-cards&type=product&q=';
      let query = '';
      productIds.forEach(productId => {
        query += `id:${productId} OR `;
      });

      if (query.endsWith(' OR ')) {
        query = query.slice(0, -4);
      }

      url = url + encodeURIComponent(query);

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch product cards');
      }

      const html = await response.text();

      const tempContainer = document.createElement('div');
      tempContainer.innerHTML = html;

      const gridItems = tempContainer.querySelectorAll('.grid__item');

      if (gridItems.length === 0) {
        this.showEmptyMessage();
        return;
      }

      gridItems.forEach(item => {
        item.classList.add('wishlist__product-item');
        this.productGrid.appendChild(item);
      });

      this.checkEmptyState();
    } catch (error) {
      console.error('Error rendering wishlist items:', error);
      this.showEmptyMessage();
    }
  }
}

customElements.define('wishlist-items', WishlistItems); 